"use client";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CommonQuestions() {
  const {
    ref: sidebarRef,
    isVisible: sidebarVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: faqRef,
    isVisible: faqVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    number: "01",
    question: "How do I request a painting estimate?",
    answer: "Estimates are provided through our digital consultation portal. After submitting your project details, our lead estimator will review your requirements and schedule a site visit to assess the scope, surface conditions, and material needs to provide an accurate, transparent quote."
  }, {
    number: "02",
    question: "Do you provide color consultation services?",
    answer: "We believe color is the soul of a space. Every project includes a professional color consultation where we analyze your lighting, architecture, and personal style to curate a palette that elevates your home's unique character."
  }, {
    number: "03",
    question: "What preparation work is included in your process?",
    answer: "Preparation is the foundation of a lasting finish. Our team performs meticulous surface cleaning, crack repair, sanding, and priming. We protect all fixtures, flooring, and furniture with professional-grade masking to ensure a clean, precise environment."
  }, {
    number: "04",
    question: "Can you handle both interior and exterior projects?",
    answer: "Yes. We specialize in comprehensive residential painting. Whether you are refreshing a single interior room or revitalizing your home's entire exterior facade, our team is equipped with the specialized tools and weather-resistant materials for every application."
  }, {
    number: "05",
    question: "What is your policy on project timelines?",
    answer: "We provide a detailed project schedule before work begins. We respect your time by maintaining a clean, efficient workspace and adhering to strict completion deadlines. Any adjustments due to weather or unforeseen conditions are communicated immediately."
  }, {
    number: "06",
    question: "Do you offer long-term maintenance plans?",
    answer: "We stand by our work and offer annual maintenance check-ups for our clients. These visits allow us to touch up high-traffic areas and ensure your home's finish remains pristine for years to come. Contact us to discuss a custom care plan."
  }];
  return <section data-section-id="3289" id="common-questions" className="bg-background py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-4">
            <div ref={sidebarRef} className={`lg:sticky lg:top-32 transition-all duration-700 ease-out ${sidebarVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <p className="text-xs uppercase tracking-[0.25em] font-mono text-primary mb-8">
                Common Questions
              </p>
              <h2 className="font-default text-foreground leading-[1.05] mb-8" style={{
              fontSize: "clamp(2.5rem, 5vw, 3.5rem)"
            }}>
                Precision,{" "}
                <span className="italic text-primary">perfected</span>.
              </h2>
              <p className="font-default text-lg leading-relaxed text-foreground/80 mb-10">
                Clear answers to the most frequent inquiries about our painting process, material standards, and the rhythm of our residential projects.
              </p>
              <div className="pt-8 border-t border-border">
                <p className="font-default text-base text-muted-foreground">
                  Still curious?{" "}
                  <Link to="/GetAQuotePage" className="italic text-foreground underline underline-offset-4 decoration-primary hover:text-primary transition-colors duration-300">
                    Request a quote
                  </Link>
                </p>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-8" ref={faqRef}>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className={`group border-b border-border last:border-b-0 border-t-0 first:border-t border-border data-[state=open]:border-l-2 data-[state=open]:border-l-primary data-[state=open]:pl-6 transition-all duration-300 ${faqVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
              transitionProperty: "opacity, transform, padding, border-color",
              transitionDuration: "700ms",
              transitionDelay: faqVisible ? `${i * 80}ms` : "0ms",
              transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
            }}>
                  <AccordionTrigger className="hover:no-underline py-6 [&>svg]:hidden group/trigger">
                    <div className="flex items-start gap-6 flex-1 text-left">
                      <span className="font-mono text-xs tracking-wider text-primary pt-2 shrink-0 w-8">
                        {faq.number}
                      </span>
                      <span className="font-default text-foreground leading-snug flex-1" style={{
                    fontSize: "clamp(1.125rem, 2vw, 1.375rem)"
                  }}>
                        {faq.question}
                      </span>
                    </div>
                    <div className="relative w-5 h-5 shrink-0 mt-3 ml-4">
                      <span className="absolute top-1/2 left-0 w-5 h-px bg-foreground -translate-y-1/2"></span>
                      <span className="absolute top-1/2 left-0 w-5 h-px bg-foreground -translate-y-1/2 rotate-90 transition-transform duration-400 ease-out group-data-[state=open]:rotate-0"></span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 pt-2">
                    <div className="flex gap-6">
                      <span className="font-mono text-xs shrink-0 w-8 opacity-0" aria-hidden="true">
                        {faq.number}
                      </span>
                      <p className="font-default leading-[1.7] text-foreground/70 max-w-2xl" style={{
                    fontSize: "clamp(1rem, 1.4vw, 1.125rem)"
                  }}>
                        {faq.answer}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}